import { useEffect } from 'react';
import { Link } from 'react-router-dom';

const sections = [
  {
    number: '1',
    title: 'Overview',
    content: (
      <>
        <p className="text-gray-600 leading-relaxed mb-4">
          OverUnder Roofing provides independent, homeowner-aligned consulting and project coordination services for roofing projects. We act solely as your advocate to help you evaluate contractor bids, improve pricing outcomes, and make informed decisions.
        </p>
        <p className="text-gray-600 leading-relaxed">
          Use of this website alone does not establish a client relationship. A client relationship begins only after you engage our services and submit payment.
        </p>
      </>
    ),
  },
  {
    number: '2',
    title: 'Services Provided',
    content: (
      <>
        <p className="text-gray-600 leading-relaxed mb-4">
          We provide consulting and coordination services, which may include:
        </p>
        <ul className="space-y-3 mb-4">
          {[
            'Collecting and coordinating 3–4 bids from vetted, licensed roofing contractors',
            'Including contractors you recommend (subject to basic vetting)',
            'Providing side-by-side comparisons of pricing, scope, materials, and warranties',
            'Offering analysis, guidance, and negotiation support',
            'Assisting with communication and general oversight during your project',
          ].map((item, i) => (
            <li key={i} className="flex items-start gap-3">
              <div className="w-5 h-5 flex items-center justify-center mt-0.5 flex-shrink-0">
                <i className="ri-checkbox-circle-line text-emerald-600 text-base"></i>
              </div>
              <span className="text-gray-600 leading-relaxed">{item}</span>
            </li>
          ))}
        </ul>
        <p className="text-gray-600 leading-relaxed">
          We do not perform roofing work, supply labor or materials, or act as a licensed contractor.
        </p>
      </>
    ),
  },
  {
    number: '3',
    title: 'Contractor Relationship',
    content: (
      <>
        <p className="text-gray-600 leading-relaxed mb-4">
          You will contract directly with the roofing contractor you select. All contractors are independent third parties.
        </p>
        <p className="text-gray-600 leading-relaxed mb-3 font-medium text-gray-700">OverUnder Roofing:</p>
        <ul className="space-y-3">
          {[
            'Does not supervise construction work',
            'Does not control contractor methods or performance',
            'Is not responsible for contractor pricing, timelines, workmanship, or outcomes',
          ].map((item, i) => (
            <li key={i} className="flex items-start gap-3">
              <div className="w-5 h-5 flex items-center justify-center mt-0.5 flex-shrink-0">
                <i className="ri-arrow-right-s-line text-emerald-600 text-base"></i>
              </div>
              <span className="text-gray-600 leading-relaxed">{item}</span>
            </li>
          ))}
        </ul>
      </>
    ),
  },
  {
    number: '4',
    title: 'Fees & Payment',
    content: (
      <>
        <div className="mb-6">
          <h4 className="font-semibold text-gray-800 mb-2">A. Project Management Fee</h4>
          <p className="text-gray-600 leading-relaxed">
            A one-time, non-refundable $500 fee is required to begin services. This covers bid coordination, analysis, and ongoing support.
          </p>
        </div>
        <div className="mb-6">
          <h4 className="font-semibold text-gray-800 mb-2">B. Success Fee</h4>
          <p className="text-gray-600 leading-relaxed mb-3">
            If you select a contractor through our process and savings are achieved, you agree to pay a Success Fee equal to the lesser of:
          </p>
          <ul className="space-y-2 mb-3">
            {[
              '5% of the final project price, or',
              '25% of the savings compared to the average of comparable bids',
            ].map((item, i) => (
              <li key={i} className="flex items-start gap-3">
                <div className="w-5 h-5 flex items-center justify-center mt-0.5 flex-shrink-0">
                  <i className="ri-arrow-right-s-line text-emerald-600 text-base"></i>
                </div>
                <span className="text-gray-600 leading-relaxed">{item}</span>
              </li>
            ))}
          </ul>
          <p className="text-gray-600 leading-relaxed">
            Savings are calculated based on bids adjusted for comparable scope, materials, and specifications ("Normalized Bids") as reasonably determined by OverUnder Roofing.
          </p>
          <p className="text-gray-600 leading-relaxed mt-2">
            The Success Fee is due within 10 days of substantial completion of the roofing work.
          </p>
        </div>
        <div>
          <h4 className="font-semibold text-gray-800 mb-2">C. No Other Compensation</h4>
          <p className="text-gray-600 leading-relaxed">
            We do not receive commissions, referral fees, or compensation from contractors. We are paid only by you.
          </p>
        </div>
      </>
    ),
  },
  {
    number: '5',
    title: 'Use of Introduced Contractors',
    content: (
      <p className="text-gray-600 leading-relaxed">
        If you hire or engage any contractor introduced through our process within six (6) months of bid delivery, the Success Fee applies.
      </p>
    ),
  },
  {
    number: '6',
    title: 'Client Responsibilities',
    content: (
      <>
        <p className="text-gray-600 leading-relaxed mb-4">You agree to:</p>
        <ul className="space-y-3 mb-4">
          {[
            'Provide accurate information about your project',
            'Respond promptly to reasonable requests',
            'Review bids and make final decisions',
          ].map((item, i) => (
            <li key={i} className="flex items-start gap-3">
              <div className="w-5 h-5 flex items-center justify-center mt-0.5 flex-shrink-0">
                <i className="ri-checkbox-circle-line text-emerald-600 text-base"></i>
              </div>
              <span className="text-gray-600 leading-relaxed">{item}</span>
            </li>
          ))}
        </ul>
        <p className="text-gray-600 leading-relaxed">
          Delays or lack of cooperation may impact timelines and outcomes.
        </p>
      </>
    ),
  },
  {
    number: '7',
    title: 'Disclaimers',
    content: (
      <ul className="space-y-3">
        {[
          'We do not guarantee pricing, savings, contractor performance, or project outcomes',
          'Contractor bids are estimates and may change',
          'We make reasonable efforts to verify licensing and insurance but do not guarantee ongoing status',
          'Roofing projects may involve unforeseen conditions that affect cost or scope',
        ].map((item, i) => (
          <li key={i} className="flex items-start gap-3">
            <div className="w-5 h-5 flex items-center justify-center mt-0.5 flex-shrink-0">
              <i className="ri-information-line text-gray-400 text-base"></i>
            </div>
            <span className="text-gray-600 leading-relaxed">{item}</span>
          </li>
        ))}
      </ul>
    ),
  },
  {
    number: '8',
    title: 'Limitation of Liability',
    content: (
      <>
        <p className="text-gray-600 leading-relaxed mb-3">
          To the fullest extent permitted by law, OverUnder Roofing shall not be liable for any indirect, incidental, or consequential damages.
        </p>
        <p className="text-gray-600 leading-relaxed">
          Our total liability under this Agreement shall not exceed the total fees paid by you.
        </p>
      </>
    ),
  },
  {
    number: '9',
    title: 'Website Use',
    content: (
      <>
        <p className="text-gray-600 leading-relaxed mb-3">
          This website is provided for informational purposes only. We do not guarantee the accuracy or completeness of information on this site.
        </p>
        <p className="text-gray-600 leading-relaxed">
          Use of this website does not create a client relationship.
        </p>
      </>
    ),
  },
  {
    number: '10',
    title: 'Termination',
    content: (
      <>
        <p className="text-gray-600 leading-relaxed mb-3">
          You may cancel services before work begins. The $500 Project Fee is non-refundable once we begin work, including initiating contractor outreach.
        </p>
        <p className="text-gray-600 leading-relaxed">
          We may terminate services if you fail to cooperate or meet payment obligations.
        </p>
      </>
    ),
  },
  {
    number: '11',
    title: 'Governing Law',
    content: (
      <p className="text-gray-600 leading-relaxed">
        This Agreement is governed by the laws of the State of North Carolina. Any disputes shall be resolved in Wake County, North Carolina.
      </p>
    ),
  },
  {
    number: '12',
    title: 'Updates to Terms',
    content: (
      <p className="text-gray-600 leading-relaxed">
        We may update these Terms from time to time. Continued use of our website or services constitutes acceptance of any updates.
      </p>
    ),
  },
  {
    number: '13',
    title: 'Acceptance',
    content: (
      <p className="text-gray-600 leading-relaxed">
        By using this website or completing payment for services, you acknowledge that you have read, understood, and agree to these Terms of Service &amp; Consulting Agreement.
      </p>
    ),
  },
];

export default function TermsPage() {
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });

    const siteUrl = import.meta.env.VITE_SITE_URL || 'https://overunderroofing.com';
    const existing = document.getElementById('terms-schema');
    if (existing) existing.remove();
    const script = document.createElement('script');
    script.id = 'terms-schema';
    script.type = 'application/ld+json';
    script.text = JSON.stringify({
      '@context': 'https://schema.org',
      '@type': 'WebPage',
      name: 'Terms of Service & Consulting Agreement – OverUnder Roofing',
      description: 'OverUnder Roofing (C&K Baker Ventures LLC) terms of service and consulting agreement. Understand our $500 flat fee, success fee structure, and service scope for Wake County roofing bid consultation.',
      url: `${siteUrl}/terms`,
      isPartOf: { '@type': 'WebSite', name: 'OverUnder Roofing', url: siteUrl },
      breadcrumb: {
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: 'Home', item: siteUrl },
          { '@type': 'ListItem', position: 2, name: 'Terms of Service', item: `${siteUrl}/terms` },
        ],
      },
      publisher: { '@type': 'Organization', name: 'OverUnder Roofing', url: siteUrl },
    });
    document.head.appendChild(script);

    return () => {
      const el = document.getElementById('terms-schema');
      if (el) el.remove();
    };
  }, []);

  return (
    <div className="min-h-screen bg-white" style={{ fontFamily: 'Inter, sans-serif' }}>
      {/* React 19 document metadata */}
      <title>Terms of Service & Consulting Agreement | OverUnder Roofing – Cary, NC</title>
      <meta name="description" content="OverUnder Roofing (C&K Baker Ventures LLC) terms of service and consulting agreement. Understand our $500 flat fee, success fee structure, and service scope for Wake County roofing bid consultation." />
      <link rel="canonical" href="https://overunderroofing.com/terms" />

      {/* Top Nav Strip */}
      <div className="bg-gray-900 text-white">
        <div className="max-w-5xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 hover:opacity-80 transition-opacity cursor-pointer">
            <i className="ri-arrow-left-line text-base text-emerald-400"></i>
            <span className="text-sm text-gray-300">Back to Home</span>
          </Link>
          <span className="text-xl font-bold" style={{ fontFamily: 'Playfair Display, serif' }}>OverUnder Roofing</span>
        </div>
      </div>

      {/* Header */}
      <div className="bg-gray-50 border-b border-gray-200">
        <div className="max-w-5xl mx-auto px-6 py-12">
          <p className="text-xs font-bold uppercase tracking-widest text-emerald-600 mb-3">Legal</p>
          <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-3" style={{ fontFamily: 'Playfair Display, serif' }}>
            Terms of Service &amp; Consulting Agreement
          </h1>
          <p className="text-sm text-gray-500">Effective Date: March 25, 2026 &nbsp;·&nbsp; C&amp;K Baker Ventures LLC d/b/a OverUnder Roofing</p>
        </div>
      </div>

      {/* Intro */}
      <div className="max-w-5xl mx-auto px-6 py-10">
        <div className="bg-emerald-50 border border-emerald-100 rounded-lg p-6 mb-10">
          <p className="text-gray-700 leading-relaxed">
            Welcome to <strong>OverUnder Roofing</strong>, a service of C&amp;K Baker Ventures LLC ("OverUnder Roofing," "we," "us," or "our"). These Terms of Service &amp; Consulting Agreement ("Agreement") govern your use of our website and consulting services.
          </p>
          <p className="text-gray-700 leading-relaxed mt-3">
            By using this website or engaging our services, you agree to the following terms.
          </p>
        </div>

        {/* Sections */}
        <div className="space-y-8">
          {sections.map((section) => (
            <div key={section.number} className="border border-gray-100 rounded-lg overflow-hidden">
              <div className="bg-gray-50 border-b border-gray-100 px-6 py-4 flex items-center gap-4">
                <span className="w-8 h-8 flex items-center justify-center rounded-full bg-emerald-600 text-white text-sm font-bold flex-shrink-0">
                  {section.number}
                </span>
                <h2 className="text-base font-bold text-gray-900 uppercase tracking-wide">{section.title}</h2>
              </div>
              <div className="px-6 py-5">
                {section.content}
              </div>
            </div>
          ))}
        </div>

        {/* Contact Footer Block */}
        <div className="mt-12 border-t border-gray-200 pt-10">
          <div className="bg-gray-900 text-white rounded-lg p-8 text-center">
            <p className="text-lg font-bold mb-1" style={{ fontFamily: 'Playfair Display, serif' }}>OverUnder Roofing</p>
            <p className="text-sm text-gray-400 mb-4">C&amp;K Baker Ventures LLC</p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4 text-sm">
              <a href="tel:+19193497701" className="text-emerald-400 hover:text-emerald-300 transition-colors cursor-pointer">
                (919) 349-7701
              </a>
              <span className="hidden sm:inline text-gray-600">·</span>
              <a href="mailto:overunderroofing@gmail.com" className="text-emerald-400 hover:text-emerald-300 transition-colors cursor-pointer">
                overunderroofing@gmail.com
              </a>
            </div>
          </div>
        </div>

        <p className="text-xs text-center text-gray-400 mt-6 mb-10">
          © 2026 OverUnder Roofing · C&amp;K Baker Ventures LLC · Wake County, NC
        </p>
      </div>
    </div>
  );
}
